import { modelManager } from '@/editor/manager/entityManager';
import { OperateWall } from './OperateWall';
import { EDirection, EEntityType, EExtremePointType, EOperateMode, ESceneMode, ISelected } from '@/editor/types';
import { TGlobalStores } from '@/editor/stores';
import { OperateWallPoint } from './OperateWallPoint';
import { OperateDoorWindow } from './OperateDoorWindow';
import { OperateStruct } from './OperateStruct';
import { EEntityStatus, IIntersectedModelEntityInfo } from '@/editor/types/entity';
import { calMousePosition, clientPosToWorldPos } from '@/editor/utils';
import { OperateAbstract } from './OperateAbstract';
import { OperateTransform } from './OperateTransform';
import { rayIntersect } from './rayIntersect';
import { OperateFurniture } from './OperateFurniture';
import { OperateSeparateWall } from './OperateSeparateWall';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getEntityCenter } from './utils';
import { CommonController2d } from '../CommonController';
import { OperateModelGroup } from './OperateModelGroup';
import { createKeyboardMoveUpdate, handOverMouseDownToMultiSelect, isInMultiModel } from '../../utils';
import { OperateAssembly } from './OperateAssembly';
import { OperateMultiModel } from './OperateMultiModel';
import { Vector2, Vector3 } from 'three';
import { EContainerType } from '@/editor/types/entity';
import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import { isMultiSelectFurnitureOrAssemblyOnly } from '@/editor/utils/select/multi-select';
import { OperateAreaTransform } from './OperateAreaTransform';
import { OperateLinearDimension } from './OperateLinearDimension';
import { KEYBOARD_MOVE_COMMIT_DELAY, directionToOffset } from '@/editor/controller/helper/keyboardMove';
import { debounce } from 'lodash-es';
import { allowMultiSelectedEntityType, BOX_SELECT_MIN_PIXEL_DISTANCE } from '../multiSelect';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';

interface IValidEntityTypeOperateMap {
  [EEntityType.Wall]: typeof OperateWall;
  [EEntityType.WallExtremePoint]: typeof OperateWallPoint;
  [EEntityType.DoorWindow]: typeof OperateDoorWindow;
  [EEntityType.Column]: typeof OperateStruct;
  [EEntityType.Transform]: typeof OperateTransform;
  [EEntityType.Furniture]: typeof OperateFurniture;
  [EEntityType.SeparateWall]: typeof OperateSeparateWall;
  [EEntityType.ModelGroup]: typeof OperateModelGroup;
  [EEntityType.Assembly]: typeof OperateAssembly;
  [EEntityType.MultiModel]: typeof OperateMultiModel;
  [EEntityType.AreaTransform]: typeof OperateAreaTransform;
  [EEntityType.LinearDimension]: typeof OperateLinearDimension;
}

type TEntityTypeOperateMap = IValidEntityTypeOperateMap & {
  [K in Exclude<EEntityType, keyof IValidEntityTypeOperateMap>]?: undefined;
};

const operateMap: TEntityTypeOperateMap = {
  [EEntityType.Wall]: OperateWall,
  [EEntityType.WallExtremePoint]: OperateWallPoint,
  [EEntityType.DoorWindow]: OperateDoorWindow,
  [EEntityType.Column]: OperateStruct,
  [EEntityType.Transform]: OperateTransform,
  [EEntityType.Furniture]: OperateFurniture,
  [EEntityType.SeparateWall]: OperateSeparateWall,
  [EEntityType.ModelGroup]: OperateModelGroup,
  [EEntityType.Assembly]: OperateAssembly,
  [EEntityType.MultiModel]: OperateMultiModel,
  [EEntityType.AreaTransform]: OperateAreaTransform,
  [EEntityType.LinearDimension]: OperateLinearDimension
};

const entitiesHandleByMouseDown = [
  // EEntityType.Wall,
  EEntityType.WallExtremePoint,
  // EEntityType.SeparateWall,
  EEntityType.Transform,
  EEntityType.AreaTransform
];

/** 支持方向键微移的实体类型 */
const keyboardMoveTypes: EEntityType[] = [
  EEntityType.Furniture,
  EEntityType.Column,
  EEntityType.Assembly,
  EEntityType.MultiModel
];

type IPickedEntityInfo = ISelected & Partial<Pick<IIntersectedModelEntityInfo, 'transformType'>>;

export class OperateEntityController extends CommonController2d {
  private _mousedownPosition: Vector2 | null = null; // 只与鼠标左键有关
  private _mousedownWorldPosition: IPoint2d | null = null; // 只与鼠标左键有关，用来量拖拽距离
  private _mousedownEvent: MouseEvent | null = null; // 只与鼠标左键有关，交接给多选时补发
  /** 上一次 KEYBOARD_PRESS_UPDATE 时多选修饰键是否按住，只在它变化时重算 hover */
  private _multiSelectModifierPressed = false;
  private _disposed = false;

  private _previousIntersectedEntityInfo: IIntersectedModelEntityInfo | null = null;
  private _currentIntersectedEntityInfo: IIntersectedModelEntityInfo | null = null;
  private _mousedownIntersectedEntityInfo: IIntersectedModelEntityInfo | null = null;
  private _mouseupIntersectedEntityInfo: IIntersectedModelEntityInfo | null = null;

  private _pickedEntityInfo: IPickedEntityInfo | null = null;
  private _currentOperate: OperateAbstract<EEntityType> | null = null;
  private _clickedOffset: IPoint2d;

  /** 方向键微移的操作实例，与鼠标拖拽的 _currentOperate 相互独立 */
  private _keyboardOperate: OperateAbstract<EEntityType> | null = null;
  private _keyboardMoveStartPoint: IPoint2d = { x: 0, y: 0 };
  private _commitKeyboardMove = debounce(() => this.finishKeyboardMove(), KEYBOARD_MOVE_COMMIT_DELAY);

  constructor(_gs: TGlobalStores) {
    super(_gs);
    this._currentOperate = null;
    this._clickedOffset = { x: 0, y: 0 };
    this._keyboardMoveUpdate = createKeyboardMoveUpdate(ESceneMode.scene2d, this.target);

    if (this._gs.editorStateStore.selected.type === EEntityType.MultiModel) {
      this.handleEntityPicked(this._gs.editorStateStore.selected);
    }
  }

  override init() {
    super.init();
    this._domElement.addEventListener('mouseleave', this.handleMouseLeave);
    this._multiSelectModifierPressed = this._gs.keyboardStore.isMultiSelectModifierPressed();
    editorEvent.on(EditorEventName.KEYBOARD_PRESS_UPDATE, this.handleKeyboardUpdate);
    // 切换可能发生在撤销执行前、或 3D 切 2D 的过渡控制器里，推迟到同步流程结束再按光标位置补 hover
    queueMicrotask(() => {
      if (!this._disposed) this.refreshHover();
    });
  }

  get globalSelected() {
    return this._gs.editorStateStore.selected;
  }

  private setGlobalSelected(selected: ISelected) {
    this._gs.editorStateStore.setSelected(selected);
  }

  private setIntersectedEntityInfo(intersectedEntityInfo: IIntersectedModelEntityInfo | null) {
    this._previousIntersectedEntityInfo = this._currentIntersectedEntityInfo;
    this._currentIntersectedEntityInfo = intersectedEntityInfo;

    if (this._gs.operateInfo.mode !== EOperateMode.EMPTY) return;

    const currentHoverEntity = this._gs.editorStateStore.hoverEntity;
    if (currentHoverEntity.entityId === (intersectedEntityInfo?.entityId || '')) {
      return;
    }

    // 更新全局状态
    this._gs.editorStateStore.setHoverEntity(
      intersectedEntityInfo || {
        entityId: '',
        type: EEntityType.EMPTY
      }
    );
  }

  /**
   * 进行射线检测，并赋值给_currentIntersectedEntityInfo
   * @param options.multiSelectOnly 只认点下去会进多选的构件，且不把多选成员并成整组
   */
  private intersectEntity(mousePosition: Vector2, options: { multiSelectOnly?: boolean } = {}): void {
    const intersectEntityInfos = rayIntersect.rayEntities(mousePosition);

    if (!intersectEntityInfos[0]) {
      this.setIntersectedEntityInfo(null);

      return;
    }

    if (options.multiSelectOnly) {
      // 修饰键下点成员是把它移出多选集，hover 要落在成员本身
      const [intersected] = intersectEntityInfos;
      this.setIntersectedEntityInfo(allowMultiSelectedEntityType.includes(intersected.type) ? intersected : null);
      return;
    }

    if (isInMultiModel(intersectEntityInfos[0])) {
      this.setIntersectedEntityInfo({
        entityId: MULTI_SELECT_TEMPORARY_ID,
        type: EEntityType.MultiModel,
        intersectPoint: new Vector3(),
        priority: 0,
        containerType: EContainerType.intersectable
      });

      return;
    }

    this.setIntersectedEntityInfo(intersectEntityInfos[0]);
  }

  // 清除上一次hover的entity状态
  private updatePreviousHoverState() {
    if (!this._previousIntersectedEntityInfo) return;

    if (this._previousIntersectedEntityInfo.isTextInfo) {
      // 如果上一个 hover 的是文本信息，直接取消之前的文本信息的 hover 状态
      if (this._previousIntersectedEntityInfo.type === EEntityType.AreaDimension) {
        modelManager.textInfoManager.updateAreaDimensionHover('');
      } else {
        modelManager.textInfoManager.updateHover('');
      }
    }

    const previousEntity = modelManager.getEntity(
      this._previousIntersectedEntityInfo.entityId,
      this._previousIntersectedEntityInfo.type
    );

    if (!previousEntity) return;

    if (previousEntity.type === EEntityType.AreaDimension) {
      previousEntity.hover = false;
    }

    if (previousEntity.selected) return;

    // TODO: 优化
    switch (previousEntity.type) {
      case EEntityType.DoorWindow:
        previousEntity.update({ status: EEntityStatus.normal });
        break;
      case EEntityType.Column: {
        previousEntity.status = EEntityStatus.normal;
        break;
      }
      case EEntityType.Furniture: {
        const furnitureData = this._gs.modelStore.getFurnitureData(previousEntity.id);
        if (furnitureData) {
          previousEntity.update({
            ...furnitureData,
            status: EEntityStatus.normal
          });
        }
        break;
      }
      case EEntityType.Transform:
        previousEntity.update({ status: EEntityStatus.normal });
        break;
      case EEntityType.LinearDimension:
        previousEntity.setHover(false);
        break;
    }
  }

  // 设置hover的entity状态
  private updateCurrentHoverState() {
    if (
      this._currentIntersectedEntityInfo?.entityId === this._previousIntersectedEntityInfo?.entityId &&
      this._currentIntersectedEntityInfo?.isTextInfo === this._previousIntersectedEntityInfo?.isTextInfo
    ) {
      // 同个entity 返回
      return;
    }

    this.updatePreviousHoverState();

    if (this._currentIntersectedEntityInfo) {
      const currentEntity = modelManager.getEntity(
        this._currentIntersectedEntityInfo.entityId,
        this._currentIntersectedEntityInfo.type
      );

      if (!currentEntity || currentEntity.selected) return;

      switch (currentEntity.type) {
        case EEntityType.DoorWindow:
          currentEntity.update({ status: EEntityStatus.hover });
          break;
        case EEntityType.Column: {
          currentEntity.status = EEntityStatus.hover;
          break;
        }
        case EEntityType.Furniture: {
          const furnitureData = this._gs.modelStore.getFurnitureData(currentEntity.id);
          if (furnitureData) {
            currentEntity.update({
              ...furnitureData,
              status: EEntityStatus.hover
            });
          }
          break;
        }
        case EEntityType.Transform: {
          currentEntity.update({
            status: EEntityStatus.hover,
            transformType: this._currentIntersectedEntityInfo.transformType
          });
          break;
        }
        case EEntityType.Room: {
          // Hover 在房间标签上时才更新标签的 hover 状态
          if (this._currentIntersectedEntityInfo.isTextInfo) {
            modelManager.textInfoManager.updateHover(this._currentIntersectedEntityInfo.entityId);
          }
          break;
        }
        case EEntityType.AreaDimension: {
          currentEntity.hover = true;
          if (this._currentIntersectedEntityInfo.isTextInfo) {
            modelManager.textInfoManager.updateAreaDimensionHover(this._currentIntersectedEntityInfo.entityId);
          }
          break;
        }
        case EEntityType.LinearDimension:
          currentEntity.setHover(true);
          break;
      }
    }
  }

  /**
   * 鼠标移出画布事件
   *
   * 清除 hover 状态
   */
  private handleMouseLeave = () => {
    this.setIntersectedEntityInfo(null);
    this.updatePreviousHoverState();
  };

  /** @param target 不传时取光标当前停留的位置，用于修饰键变化、控制器切换等鼠标没动的场景 */
  private refreshHover = (target: Pick<MouseEvent, 'clientX' | 'clientY'> | null = null) => {
    const pointer = target ?? this._gs.pointerStateStore.getIdlePointer();
    if (!pointer) return;

    this.intersectEntity(calMousePosition(pointer, this._domElement), {
      multiSelectOnly: this._gs.keyboardStore.isMultiSelectModifierPressed()
    });
    this.updateCurrentHoverState();
  };

  private handleKeyboardUpdate = () => {
    const pressed = this._gs.keyboardStore.isMultiSelectModifierPressed();
    if (pressed === this._multiSelectModifierPressed) return;

    this._multiSelectModifierPressed = pressed;
    this.refreshHover();
  };

  // 负责维护 _pickedEntityInfo 和 editorStateStore.selected
  private handleEntityPicked(pickedEntityInfo: IPickedEntityInfo | null) {
    this._pickedEntityInfo = pickedEntityInfo;
    if (!this._pickedEntityInfo) {
      this.setGlobalSelected({ entityId: '', type: EEntityType.EMPTY });
      return;
    }

    if (this._pickedEntityInfo.type === EEntityType.MultiModel) {
      // 解析多选选中的是多模型还是单模型再将选中信息重置
      const multiModelData = this._gs.getDataByIdType(this._pickedEntityInfo.entityId, this._pickedEntityInfo.type);
      if (multiModelData) {
        if (multiModelData.modelInfos.length === 1) {
          this._pickedEntityInfo = {
            entityId: multiModelData.modelInfos[0].id,
            type: multiModelData.modelInfos[0].type
          };
        }
      }
    }

    // 墙端点不修改selected数据
    if (this._pickedEntityInfo.type === EEntityType.WallExtremePoint) return;

    // 形变控件不修改selected数据
    if (this._pickedEntityInfo.type === EEntityType.Transform) return;
    if (this._pickedEntityInfo.type === EEntityType.AreaTransform) return;

    // 端点处理
    const selectedInfo = { ...this._pickedEntityInfo };

    this.setGlobalSelected(selectedInfo);
  }

  private canStartToDrag(intersectedEntityInfo: IPickedEntityInfo | null) {
    if (!this._pickedEntityInfo || !intersectedEntityInfo) return false;

    // 多选临时数据判断
    if (isInMultiModel(this._pickedEntityInfo) && isInMultiModel(intersectedEntityInfo)) {
      if (this._pickedEntityInfo.type === EEntityType.DoorWindow) {
        return false;
      }

      return isMultiSelectFurnitureOrAssemblyOnly(this._gs.getDataByIdType('', EEntityType.MultiModel));
    }

    return this._pickedEntityInfo.entityId === intersectedEntityInfo.entityId;
  }

  private isIntersectedArcMidPoint(intersectedEntityInfo: IPickedEntityInfo | null) {
    if (intersectedEntityInfo && intersectedEntityInfo.type === EEntityType.WallExtremePoint) {
      const wallExtremePointEntity = modelManager.wallExtremePointManager.getEntity(intersectedEntityInfo.entityId);
      if (wallExtremePointEntity?.getData().pointType === EExtremePointType.ArcMidPoint) {
        return true;
      }
    }

    return false;
  }

  /**
   * 方向键微移选中实体
   *
   * 与拖拽一样走虚拟模型，连续按键只在最后提交一次，区别是微移不走吸附
   */
  public override moveEntityByDirection(direction: EDirection) {
    // 鼠标拖拽过程中不响应
    if (this._currentOperate) return;

    if (!this._keyboardOperate && !this.startKeyboardMove()) return;

    this._keyboardOperate?.moveByOffset(directionToOffset(direction));
    // 每次按键都把提交时机往后推
    this._commitKeyboardMove();
  }

  /** 目前需要结算的只有延时提交的方向键微移，没有进行中的微移时什么都不做 */
  public override flushSync() {
    this._commitKeyboardMove.flush();
  }

  private startKeyboardMove() {
    const { entityId, type } = this._gs.editorStateStore.selected;
    if (!keyboardMoveTypes.includes(type)) return false;

    // 与拖拽同一条件：只有纯家具/组合的多选可以移动
    if (
      type === EEntityType.MultiModel &&
      !isMultiSelectFurnitureOrAssemblyOnly(this._gs.getDataByIdType('', EEntityType.MultiModel))
    ) {
      return false;
    }

    // 多选的数据不按 entityId 存放，统一用空 id 取
    const center =
      type === EEntityType.MultiModel
        ? this._gs.getDataByIdType('', EEntityType.MultiModel)?.center
        : getEntityCenter({ entityId, type });
    if (!center) return false;

    const OperateClass = operateMap[type];
    if (!OperateClass) return false;

    this._keyboardOperate = new OperateClass();
    this._keyboardOperate.checkedEntity = modelManager.getEntity(entityId, type);
    this._keyboardMoveStartPoint = center;

    // 修改全局移动操作状态标记
    this._gs.editorStateStore.moving = true;
    this._keyboardOperate.startMove(center);

    return true;
  }

  private finishKeyboardMove() {
    if (!this._keyboardOperate) return;

    // 所有支持微移的实体都用移动过程中记录的虚拟数据提交，传入的点不参与计算
    this._keyboardOperate.completeMove(this._keyboardMoveStartPoint);
    this._keyboardOperate.dispose();
    this._keyboardOperate = null;

    this._gs.editorStateStore.moving = false;
  }

  /**
   * 起手点是否落在当前选中构件的变换框内
   *
   * 组合、多选这类构件之间有空隙，按在空隙上射线打不到实体，但用户的意图是拖动这个选中的东西
   * （典型场景：框选家具成组后立即拖动组合）。这段区域不能被当成空白区抢去框选
   */
  private isMouseDownInSelectedBox() {
    if (!this._mousedownWorldPosition) return false;
    if (!modelManager.transformManager.visible) return false;

    const { center, width, height, rotation } = modelManager.transformManager.getData();
    // 把起手点转回框自身的坐标系，再按轴对齐比较
    const local = Point2dUtils.rotatePoint(this._mousedownWorldPosition, center, -rotation);

    return Math.abs(local.x - center.x) <= width / 2 && Math.abs(local.y - center.y) <= height / 2;
  }

  private startOperate(worldPosition: IPoint2d) {
    if (!this._pickedEntityInfo) return;

    const { entityId, type: entityType, transformType } = this._pickedEntityInfo;

    const entityCenter = getEntityCenter(this._pickedEntityInfo);
    if (entityCenter) {
      this._clickedOffset.x = entityCenter.x - worldPosition.x;
      this._clickedOffset.y = entityCenter.y - worldPosition.y;
    } else {
      this._clickedOffset.x = 0;
      this._clickedOffset.y = 0;
    }

    const OperateClass = operateMap[entityType];
    if (!OperateClass) return;
    this._currentOperate = new OperateClass();

    switch (entityType) {
      case EEntityType.Transform: {
        // 如果是形变操作，传入形变类型
        if (transformType) {
          (this._currentOperate as OperateTransform).transformType = transformType;
        }
        break;
      }
      case EEntityType.AreaTransform: {
        (this._currentOperate as OperateAreaTransform).controlPointIds = entityId.split('-');
        break;
      }
      case EEntityType.WallExtremePoint: {
        // 选中墙体两端的点后之前的选中模型要清除，但是选中弧形墙的中点后之前选中的弧形状态要保持不变
        const entity = modelManager.getEntity(entityId, entityType);
        if (entity?.getData().pointType !== EExtremePointType.ArcMidPoint) {
          this.setGlobalSelected({
            entityId: '',
            type: EEntityType.EMPTY
          });
        }
        break;
      }
    }

    this._currentOperate.checkedEntity = modelManager.getEntity(entityId, entityType);

    // 修改全局移动操作状态标记
    this._gs.editorStateStore.moving = true;
    this._currentOperate.pickPoint = worldPosition;
    this._currentOperate.startMove(worldPosition);
  }

  private completeOperate(worldPosition: IPoint2d) {
    if (!this._currentOperate) return;

    this._currentOperate.completeMove(Point2dUtils.added(worldPosition, this._clickedOffset));
    this._currentOperate.dispose();
    this._currentOperate = null;

    // 修改全局移动操作状态标记
    this._gs.editorStateStore.moving = false;
  }

  // mouseup3种行为：1.框选结束；2.移动entity结束；3.如mouseup没pick，结束pick
  protected override mouseUp = (e: MouseEvent) => {
    if (e.button !== 0) {
      this._gs.editorStateStore.dragging = false;
      return;
    }

    const mouseupPosition = calMousePosition(e, this._domElement);
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);

    // ==== entity移动结束或多选模式结束 ====
    if (this._currentOperate) {
      this.completeOperate(worldPosition);
      return;
    }

    // ==== 结束pick ====
    // 如果当前mousedown已经决定有选中，则退出
    if (this._selectedWhenMouseDown) return;

    // 记录mouseup时选中的entity
    this.intersectEntity(mouseupPosition);
    this._mouseupIntersectedEntityInfo = this._currentIntersectedEntityInfo;
    this.updatePreviousHoverState();

    // 判断mousedown和mouseup是否选中同一个entity
    const pickedEntityInfo =
      this._mousedownIntersectedEntityInfo?.entityId === this._mouseupIntersectedEntityInfo?.entityId &&
      this._mouseupIntersectedEntityInfo;

    if (pickedEntityInfo) {
      this.handleEntityPicked(pickedEntityInfo);
    } else {
      this.handleEntityPicked(null);
    }
  };

  private _selectedWhenMouseDown = false;

  // 左键mousedown产生3种行为: 1.开始移动操作；2.pick；3.等待后续mousemove、mouseup（pick、框选）；
  protected override mouseDown = (e: MouseEvent) => {
    // 鼠标操作会改变选中状态，先结算掉延时提交的数据
    this.flushSync();

    this._selectedWhenMouseDown = false;
    this._mousedownPosition = null;
    this._mousedownWorldPosition = null;
    this._mousedownEvent = null;

    if (e.button === 2 || e.button === 1) {
      // 右键拖拽操作
      this._gs.editorStateStore.dragging = true;
      return;
    }

    if (e.button !== 0) return;

    this._mousedownPosition = calMousePosition(e, this._domElement);
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);
    this._mousedownWorldPosition = worldPosition;
    this._mousedownEvent = e;

    // 多选修饰键按住时左键直接进多选（点选加减或框选）。
    // 不能在 keydown 就切模式，否则 Ctrl+C / ⌘C 这类快捷键会因为菜单卸载而失效
    if (this._gs.keyboardStore.isMultiSelectModifierPressed()) {
      handOverMouseDownToMultiSelect(e);
      return;
    }

    this.intersectEntity(this._mousedownPosition);
    // 记录mousedown时选中的entity
    this._mousedownIntersectedEntityInfo = this._currentIntersectedEntityInfo;
    this.updatePreviousHoverState();

    // 弧形墙的中点可以直接拖动
    if (this.isIntersectedArcMidPoint(this._mousedownIntersectedEntityInfo)) {
      this._selectedWhenMouseDown = true;
      this.handleEntityPicked(this._currentIntersectedEntityInfo);
      this.startOperate(worldPosition);
      return;
    }

    // ==== 可移动判断 ====
    if (this.canStartToDrag(this._mousedownIntersectedEntityInfo)) {
      // 避免删除撤销后，选中状态已重置但 controller 的 picked 状态还保留之前的选中状态
      // TODO: 考虑与下面选中判断合并简化逻辑
      if (
        this._pickedEntityInfo?.type !== EEntityType.Transform &&
        (this.globalSelected.entityId !== this._pickedEntityInfo?.entityId ||
          this.globalSelected.type !== this._pickedEntityInfo.type)
      ) {
        this.handleEntityPicked(null);
      } else {
        this.startOperate(worldPosition);
        return;
      }
    }

    // ==== 选中判断 ====
    // 墙体在mousedown时可直接被选中
    if (this._currentIntersectedEntityInfo) {
      if (entitiesHandleByMouseDown.includes(this._currentIntersectedEntityInfo.type)) {
        this._selectedWhenMouseDown = true;
        this.handleEntityPicked(this._currentIntersectedEntityInfo);
      } else if (
        (this._pickedEntityInfo?.type === EEntityType.Transform ||
          this._pickedEntityInfo?.type === EEntityType.AreaTransform) &&
        this._currentIntersectedEntityInfo.entityId === this._gs.editorStateStore.selected.entityId
      ) {
        // 使用 tranform 组件调整后，会导致 _pickedEntityInfo 和 _currentIntersectedEntityInfo 不一致
        this.handleEntityPicked(this._currentIntersectedEntityInfo);
      } else if (
        this._pickedEntityInfo?.type === EEntityType.MultiModel &&
        this._currentIntersectedEntityInfo.type === EEntityType.Assembly &&
        this._gs.editorStateStore.multiSelectTemporary === null
      ) {
        // 家具成组后保持选中状态，此时 _pickedEntityInfo 还是之前的多选状态
        this.handleEntityPicked(this._currentIntersectedEntityInfo);
      } else if (
        (this._currentIntersectedEntityInfo.type === EEntityType.Wall ||
          this._currentIntersectedEntityInfo.type === EEntityType.SeparateWall) &&
        this.isIntersectedArcMidPoint(this._pickedEntityInfo)
      ) {
        // 这里主要是保证选中弧形墙的中点拖动了之后，此时弧形墙还是选中状态可以继续移动弧形墙
        this.handleEntityPicked(this._currentIntersectedEntityInfo);
      }
    } else {
      this.handleEntityPicked(null);
    }

    // ==== 可移动二次判断 ====
    if (this.canStartToDrag(this._mousedownIntersectedEntityInfo)) {
      this.startOperate(worldPosition);
      return;
    }
  };

  // mousemove3种行为：1.hover；2.移动entity；3.hover；
  protected override mouseMove = (e: MouseEvent) => {
    // ==== hover ====
    if (e.buttons === 0) {
      // 鼠标移动，无任何鼠标键按下时
      this.refreshHover(e);
      return;
    }

    if (e.buttons !== 1) return;
    if (!this._mousedownPosition) return;

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);

    // ==== 框选 ====
    // 左键拖拽但没在移动构件，拖够距离就转成框选。
    // 距离阈值与多选自己区分点选/框选的那个是同一个，同样按屏幕像素量，小幅抖动仍走原来的点选路径
    if (!this._currentOperate) {
      if (
        this._mousedownEvent &&
        !this.isMouseDownInSelectedBox() &&
        Point2dUtils.distanceTo(
          { x: this._mousedownEvent.clientX, y: this._mousedownEvent.clientY },
          { x: e.clientX, y: e.clientY }
        ) >= BOX_SELECT_MIN_PIXEL_DISTANCE
      ) {
        // 补发的是按下那一刻的事件，框选起点才落在起手位置而不是当前位置
        handOverMouseDownToMultiSelect(this._mousedownEvent);
      }
      return;
    }

    // ==== 移动entity ====
    this._currentOperate.move(Point2dUtils.added(worldPosition, this._clickedOffset));
  };

  clear() {
    // 控制器销毁前结算掉延时提交的数据，否则微移中的虚拟模型连带真实实体的隐藏状态一起丢失
    this.flushSync();

    if (this._currentOperate) {
      this._currentOperate.dispose();
      this._currentOperate = null;
    }

    this.updatePreviousHoverState();
    this._previousIntersectedEntityInfo = null;
    this._currentIntersectedEntityInfo = null;
    this._mousedownIntersectedEntityInfo = null;
    this._mouseupIntersectedEntityInfo = null;

    this._gs.editorStateStore.moving = false;
  }

  override dispose() {
    this._disposed = true;
    this._domElement.removeEventListener('mouseleave', this.handleMouseLeave);
    editorEvent.off(EditorEventName.KEYBOARD_PRESS_UPDATE, this.handleKeyboardUpdate);

    this.clear();
    super.dispose();
  }
}
